import { useCallback, useEffect, useState } from 'react';
import { useTheme, useTokens } from '../theme.jsx';
import { getSeats, listInstitutionUsers, revokeSeat } from '../services/institutions.js';
import {
  Button, Card, CheckGlyph, ErrorState, Field, SectionTitle, Stat, StatRow,
} from '../uiKit.jsx';
import { notifyError, notifySuccess } from '../toast.js';
import { TableSkeleton } from '../Skeleton.jsx';
import { marketingHomeUrl } from '../../portal/portalUrl.js';

const fmt = (value) => { return Number(value || 0).toLocaleString(); };
const fmtDate = (value) => {
  if (!value) { return 'Not available'; }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) { return 'Not available'; }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const activityLabel = (lastSeenAt) => {
  if (!lastSeenAt) { return 'No recent activity'; }
  const days = Math.max(0, (Date.now() - new Date(lastSeenAt).getTime()) / 86400000);
  if (days <= 7) { return 'Active this week'; }
  if (days <= 30) { return 'Active this month'; }
  return `Last active ${fmtDate(lastSeenAt)}`;
};

// A student joins by signing in to the main app with Google on their school
// address (membership is matched by verified email domain) and accepting the
// school-sponsored plan under Settings > Plan & Billing. There is no per-school
// join link, so this is the sign-in page itself.
const studentSignInUrl = () => { return `${marketingHomeUrl()}/login`; };

const announcementText = (link, domain) => {
  return [
    'Our school now sponsors Coffee Chats for students. Use it to find alumni in the fields you are exploring, plan your outreach, and prepare for coffee chats.',
    `To join, go to ${link}, choose Continue with Google, and sign in with your school Google account (your @${domain} address). If you already use Coffee Chats with a personal email, sign in with your school account instead.`,
    'Then open Settings > Plan & Billing and select Accept sponsorship.',
  ].join('\n\n');
};

// Day one: nobody has opted in yet. Gives the admin the sign-in link and a
// ready announcement to send, each with its own copy button.
function InviteFirstCohort({ domain }) {
  const t = useTokens();
  const [copied, setCopied] = useState('');
  const link = studentSignInUrl();
  const announcement = announcementText(link, domain);

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
    } catch {
      notifyError('Could not copy. Select the text and copy it instead.');
    }
  };

  return (
    <div className="py-2">
      <p className="text-base font-medium" style={{ color: t.heading, fontFamily: t.fonts.body }}>
        No students yet. Invite your first cohort.
      </p>
      <h3 className="mt-4 text-base font-medium" style={{ color: t.heading, fontFamily: t.fonts.body }}>How students join</h3>
      <p className="mt-1 text-base" style={{ color: t.muted, fontFamily: t.fonts.body }}>
        Students sign in to Coffee Chats with their school email address. Sign-up uses Google, so students need a Google account on their school address. They accept the school-sponsored plan under Settings &gt; Plan &amp; Billing (a prompt may also appear on the dashboard on a later visit), and appear here once they accept.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button label="Copy student sign-up link" onClick={() => { copy('link', link); }} />
        <span className="text-[13px] break-all" style={{ color: t.muted, fontFamily: t.fonts.mono }}>{link}</span>
        <span role="status" className="text-sm" style={{ color: t.success }}>{copied === 'link' ? 'Copied' : ''}</span>
      </div>
      <h3 className="mt-6 text-base font-medium" style={{ color: t.heading, fontFamily: t.fonts.body }}>Announcement to send students</h3>
      <p
        className="mt-2 whitespace-pre-line rounded-md border p-3 text-base"
        style={{ color: t.body, borderColor: t.hairline, fontFamily: t.fonts.body }}
      >
        {announcement}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button variant="secondary" label="Copy announcement" onClick={() => { copy('announcement', announcement); }} />
        <span role="status" className="text-sm" style={{ color: t.success }}>{copied === 'announcement' ? 'Copied' : ''}</span>
      </div>
    </div>
  );
}

function UsageDetails({ usage, compact = false }) {
  const t = useTokens();
  if (!usage) { return <span className="text-xs" style={{ color: t.muted }}>No metered usage yet</span>; }
  const research = (usage.standardResearchUsed || 0) + (usage.deepResearchUsed || 0);
  const practice = (usage.aiAvatarMinutesUsed || 0) + (usage.aiVoiceAgentMinutesUsed || 0);
  return (
    <details className={compact ? 'min-w-0' : 'min-w-[180px]'}>
      <summary className="cursor-pointer text-sm font-medium" style={{ color: t.heading }}>
        {fmt(usage.aiCreditsUsed)} credits · {fmt(research)} research
      </summary>
      <div className={compact ? 'mt-3 grid grid-cols-2 gap-2' : 'mt-3 min-w-[520px] max-w-[720px]'}>
        {compact ? [
          ['Coffee credits', usage.aiCreditsUsed],
          ['Contacts', usage.contactsReached],
          ['Projects', usage.projectsCreated],
          ['Practice min', practice],
          ['Standard research', usage.standardResearchUsed],
          ['Deep research', usage.deepResearchUsed],
          ['AI allowance tokens', usage.aiChatTokensUsed],
          ['Overage credits', usage.overageCreditsUsed],
        ].map(([label, value]) => {
          return (
            <div key={label} className="rounded-md border p-2" style={{ borderColor: t.hairline }}>
              <div className="text-[10px] uppercase tracking-wide" style={{ color: t.muted }}>{label}</div>
              <div className="mt-1 text-sm" style={{ color: t.heading, fontFamily: t.fonts.mono }}>{fmt(value)}</div>
            </div>
          );
        }) : (
          <StatRow>
            <Stat label="Coffee credits" value={fmt(usage.aiCreditsUsed)} />
            <Stat label="Contacts" value={fmt(usage.contactsReached)} />
            <Stat label="Projects" value={fmt(usage.projectsCreated)} />
            <Stat label="Practice min" value={fmt(practice)} />
            <Stat label="Standard research" value={fmt(usage.standardResearchUsed)} />
            <Stat label="Deep research" value={fmt(usage.deepResearchUsed)} />
            <Stat label="AI allowance tokens" value={fmt(usage.aiChatTokensUsed)} />
            <Stat label="Overage credits" value={fmt(usage.overageCreditsUsed)} />
          </StatRow>
        )}
        <p className="mt-2 text-[11px]" style={{ color: t.muted, fontFamily: t.fonts.mono }}>
          {usage.tier || 'Plan'} usage · {fmtDate(usage.periodStart)} to {fmtDate(usage.periodEnd)}
        </p>
      </div>
    </details>
  );
}

/**
 * `schoolDomain` is the school's first verified email domain (portal only).
 * Without it, or without an available seat, the day-one invite is held back:
 * students who followed it would get no offer or a "seats are full" card.
 */
export default function StudentRosterPanel({ institutionId, onChange, schoolDomain = null }) {
  const t = useTokens();
  const portal = useTheme() === 'portal';
  const [data, setData] = useState(null);
  // Portal only: available seats, null until read, false when the read failed.
  const [seatsAvailable, setSeatsAvailable] = useState(null);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('opted_in');
  const [busyUserId, setBusyUserId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      setData(await listInstitutionUsers(institutionId, page, search, view));
    } catch (err) {
      setData(null);
      setError(err?.response?.data?.error || 'Failed to load students.');
    }
  }, [institutionId, page, search, view]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!portal) { return undefined; }
    let active = true;
    getSeats(institutionId)
      .then((result) => { if (active) { setSeatsAvailable(Number(result?.seats?.available) || 0); } })
      .catch(() => { if (active) { setSeatsAvailable(false); } });
    return () => { active = false; };
  }, [portal, institutionId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(query.trim());
    }, 250);
    return () => { clearTimeout(timer); };
  }, [query]);

  const visibleUsers = data?.users || [];
  const optedIn = view === 'opted_in';
  // The day-one invite block is school-facing copy, so the console drill-in
  // keeps the plain empty line.
  const dayOne = portal && Boolean(data) && optedIn && !search && data.total === 0;
  // Hold the invite until seats are read; a failed read falls back to the
  // plain empty line.
  const dayOneBlocker = (() => {
    if (!dayOne || seatsAvailable === null || seatsAvailable === false) { return null; }
    if (!schoolDomain) {
      return 'Students cannot join yet: your school has no verified email domain, so students who sign in are not offered the sponsored plan. Contact Coffee Chats support to verify your domain before inviting students.';
    }
    if (seatsAvailable <= 0) {
      return 'Add seats before inviting students. Students who sign in now will see that seats are full.';
    }
    return '';
  })();
  const showInvite = dayOneBlocker === '';

  const changeView = (nextView) => {
    setView(nextView);
    setPage(1);
    setQuery('');
    setSearch('');
    setData(null);
    setError('');
  };

  const revoke = async (user) => {
    const name = user.preferredName || user.name || user.email;
    const confirmed = typeof globalThis.confirm !== 'function'
      || globalThis.confirm(`Revoke the sponsored seat for ${name}?`);
    if (!confirmed) { return; }
    setBusyUserId(user._id);
    setError('');
    try {
      await revokeSeat(user._id, institutionId);
      notifySuccess(`Seat revoked for ${user.email}.`);
      await load();
      onChange?.();
    } catch (err) {
      notifyError(err?.response?.data?.error || 'Failed to revoke the seat.');
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <Card>
      <SectionTitle right={(
        <span className="text-xs" style={{ color: t.muted, fontFamily: t.fonts.mono }}>
          {data ? `${data.total} ${optedIn
            ? `opted-in student${data.total === 1 ? '' : 's'}`
            : `account${data.total === 1 ? '' : 's'} awaiting opt-in`}` : null}
        </span>
      )}>
        Students
      </SectionTitle>
      <p className="-mt-3 mb-4 text-sm" style={{ color: t.muted }}>
        {optedIn
          ? 'Manage currently opted-in, institution-funded students. Usage is limited to entitlement counters; student content stays private.'
          : 'See account holders using a verified school email who have not opted in. Only name, email, and account creation date are shown.'}
      </p>
      <div className="mb-4 flex flex-wrap gap-2" aria-label="Student account status">
        {[
          ['opted_in', 'Opted in'],
          ['not_opted_in', 'Not opted in'],
        ].map(([key, label]) => {
          const active = view === key;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={active}
              onClick={() => { changeView(key); }}
              className={`inline-flex items-center min-h-[44px] rounded-full border px-4 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 ${t.focusRing}`}
              style={{
                borderColor: active ? t.pillActiveBorder : t.pillBorder,
                backgroundColor: active ? t.pillActiveBg : t.pillBg,
                color: active ? t.pillActiveText : t.pillText,
              }}
            >
              {/* Same selected chip as uiKit Pill: a check, so selection is never color alone. */}
              {portal && active ? <CheckGlyph /> : null}
              {label}
            </button>
          );
        })}
      </div>
      <div className="mb-3">
        <Field value={query} onChangeText={setQuery} placeholder="Search students by name or email" className="mb-0" />
      </div>
      {error ? <ErrorState className="mb-3" message={error} onRetry={load} /> : null}
      {!data && !error ? <TableSkeleton rows={5} columns={5} /> : null}
      {showInvite ? <InviteFirstCohort domain={schoolDomain} /> : null}
      {dayOneBlocker ? <p className="text-base" style={{ color: t.body }}>{dayOneBlocker}</p> : null}
      {data && visibleUsers.length === 0 && !(dayOne && seatsAvailable !== false) ? (
        <p className="text-sm" style={{ color: t.muted }}>
          {search
            ? 'No students match this search.'
            : optedIn ? 'No students have opted in yet.' : 'Every school-domain account has opted in.'}
        </p>
      ) : null}
      {visibleUsers.length ? (
        <>
          <div className="grid gap-3 md:hidden">
            {visibleUsers.map((user) => {
              const active = optedIn && user.subscription?.sponsoredStatus === 'ACTIVE';
              return (
                <article key={`${user._id}-mobile`} className="rounded-xl border p-4" style={{ borderColor: t.border, backgroundColor: t.surface }}>
                  <div className="text-sm font-semibold" style={{ color: t.heading }}>{user.preferredName || user.name || user.email}</div>
                  <div className="mt-1 break-all text-[11px]" style={{ color: t.muted, fontFamily: t.fonts.mono }}>{user.email}</div>
                  <dl className="mt-4 grid grid-cols-2 gap-3">
                    <div><dt className="text-[10px] uppercase tracking-wide" style={{ color: t.muted }}>{optedIn ? 'Access' : 'Program access'}</dt><dd className="mt-1 text-xs" style={{ color: t.body }}>{optedIn ? `${user.subscription?.sponsoredTier || 'No sponsored tier'} · ${user.subscription?.sponsoredStatus || 'INACTIVE'}` : 'Not opted in'}</dd></div>
                    <div><dt className="text-[10px] uppercase tracking-wide" style={{ color: t.muted }}>{optedIn ? 'Activity' : 'Account created'}</dt><dd className="mt-1 text-xs" style={{ color: t.body }}>{optedIn ? activityLabel(user.settings?.lastSeenAt) : fmtDate(user.createdAt)}</dd></div>
                  </dl>
                  {optedIn ? <div className="mt-4"><UsageDetails usage={user.usage} compact /></div> : null}
                  {active ? <div className="mt-4"><Button small variant="secondary" label={busyUserId === user._id ? 'Revoking…' : 'Revoke seat'} disabled={Boolean(busyUserId)} onClick={() => { revoke(user); }} /></div> : null}
                </article>
              );
            })}
          </div>
          <div className="hidden md:block">
          <table className="w-full border-collapse">
            <thead><tr>
              {(optedIn
                ? ['Student', 'Access', 'Activity', 'Current usage', '']
                : ['Student', 'Account created', 'Program access']).map((label) => {
                return (
                  <th key={label || 'actions'} scope="col" className="border-b py-2 pr-3 text-left text-[11px] font-normal uppercase tracking-wider last:pr-0 last:text-right" style={{ color: t.muted, borderColor: t.hairline, fontFamily: t.fonts.mono }}>
                    {label}
                  </th>
                );
              })}
            </tr></thead>
            <tbody>
              {visibleUsers.map((user) => {
                const active = optedIn && user.subscription?.sponsoredStatus === 'ACTIVE';
                return (
                  <tr key={user._id}>
                    <td className="border-b py-3 pr-3 align-top" style={{ borderColor: t.hairline }}>
                      <div className="text-sm font-medium" style={{ color: t.heading }}>{user.preferredName || user.name || user.email}</div>
                      <div className="mt-0.5 text-[11px]" style={{ color: t.muted, fontFamily: t.fonts.mono }}>{user.email}</div>
                    </td>
                    {optedIn ? (
                      <>
                        <td className="border-b py-3 pr-3 align-top text-xs" style={{ color: t.body, borderColor: t.hairline, fontFamily: t.fonts.mono }}>
                          <div>{user.subscription?.sponsoredTier || 'No sponsored tier'}</div>
                          <div className="mt-1" style={{ color: active ? t.success : t.muted }}>{user.subscription?.sponsoredStatus || 'INACTIVE'}</div>
                        </td>
                        <td className="border-b py-3 pr-3 align-top text-xs" style={{ color: t.body, borderColor: t.hairline }}>
                          <div>{activityLabel(user.settings?.lastSeenAt)}</div>
                          <div className="mt-1" style={{ color: t.muted }}>Joined {fmtDate(user.createdAt)}</div>
                        </td>
                        <td className="border-b py-3 pr-3 align-top" style={{ borderColor: t.hairline }}><UsageDetails usage={user.usage} /></td>
                        <td className="border-b py-2 text-right align-top" style={{ borderColor: t.hairline }}>
                          {active ? <Button small variant="secondary" label={busyUserId === user._id ? 'Revoking…' : 'Revoke seat'} disabled={Boolean(busyUserId)} onClick={() => { revoke(user); }} /> : null}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="border-b py-3 pr-3 align-top text-xs" style={{ color: t.body, borderColor: t.hairline }}>{fmtDate(user.createdAt)}</td>
                        <td className="border-b py-3 align-top text-xs" style={{ color: t.muted, borderColor: t.hairline }}>Not opted in</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </>
      ) : null}
      {(data?.totalPages || 0) > 1 ? (
        <div className="mt-4 flex items-center justify-between">
          <Button small variant="secondary" label="Previous" disabled={page <= 1} onClick={() => { setPage((value) => { return value - 1; }); }} />
          <span className="text-xs" style={{ color: t.muted, fontFamily: t.fonts.mono }}>Page {page} of {data.totalPages}</span>
          <Button small variant="secondary" label="Next" disabled={page >= data.totalPages} onClick={() => { setPage((value) => { return value + 1; }); }} />
        </div>
      ) : null}
    </Card>
  );
}
